import axios from "axios";
import Hex from "crypto-js/enc-hex";
import HmacSHA256 from "crypto-js/hmac-sha256";
import environment, { getRunTimeConfig, getProcessEnvConfig } from "@/environment";
import environmentVariables from '../../environmentVariables'

const client = axios.create({
  baseURL: environment.VUE_APP_API_URL,
  headers: { "X-Custom-Header": "foobar" },
});

function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function getEnvVariable(varName: string, retryTimes = 30) {
  let count = 0;

  do {
    const localEnv: any = await import("@/environment");
    const processEnvConfigObj: any = getProcessEnvConfig();
    const runTimeConfigObj: any = getRunTimeConfig();
    const variable =
      localEnv.default[varName] ??
      processEnvConfigObj[varName] ??
      runTimeConfigObj[varName];

    if (variable != undefined) {
      return variable;
    } else {
      await timeout(1000);
      count++;
    }
  } while (count < retryTimes);

  return undefined;
}

type SignedRequestParams = {
  method: string;
  path: string;
  data?: unknown;
};

type SignedRequestHeaders = {
  authorization: string;
  body: string;
  timestamp: string;
};

export function buildSignedRequestHeaders({
  method,
  path,
  data,
}: SignedRequestParams): SignedRequestHeaders {
  const body = JSON.stringify(data ?? {});
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const signaturePayload = [method.toUpperCase(), path, timestamp, body].join("\n");
  const apiKey = environment.VUE_APP_API_KEY || "";
  const authorization = Hex.stringify(HmacSHA256(signaturePayload, apiKey));

  return {
    authorization,
    body,
    timestamp,
  };
}

client.interceptors.request.use(
  async function (config: any) {
    config.baseURL = await getEnvVariable("VUE_APP_API_URL");
    return config;
  },
  function (error) {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    );
  }
);

client.interceptors.response.use(
  async function (config) {
    const VUE_APP_IS_PRODUCTION = await getEnvVariable("VUE_APP_IS_PRODUCTION");

    if (VUE_APP_IS_PRODUCTION === true)  {
      const appVersion = environmentVariables.APP_VERSION;
      const apiVersion = config.data.api_version;

      const [appMajor, appMinor, appPatch] = appVersion.split('.');
      const [apiMajor, apiMinor, apiPatch] = apiVersion.split('.');

      if (
          apiVersion &&
          appMajor === apiMajor &&
          appMinor === apiMinor &&
          appPatch === apiPatch
        ) {
        return config;
      }
      return window.location.reload();
    }
    return config;
  }, function (error) {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    );
  }
);


export default client;
